import { useState } from "react";
import { User, Calendar, AlertTriangle } from "lucide-react";
import { StatusBadge } from "@/components/common/StatusBadge";
import { RelevanceScore } from "./RelevanceScore";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INVESTOR_STAGES, priorityBadge, gbp, isOverdueFollowup, hasNoNextAction } from "@/data/investorOptions";

export const InvestorKanban = ({ investors, today, onOpen, onStageChange }) => {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const drop = (stage) => {
    if (dragId) {
      const inv = investors.find((x) => x.id === dragId);
      if (inv && inv.stage !== stage) onStageChange(dragId, stage);
    }
    setDragId(null); setOverCol(null);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-3" data-testid="investor-kanban">
      {INVESTOR_STAGES.map((col) => {
        const cards = investors.filter((i) => i.stage === col);
        return (
          <div
            key={col}
            data-testid={`inv-kanban-col-${col}`}
            onDragOver={(e) => { e.preventDefault(); setOverCol(col); }}
            onDrop={() => drop(col)}
            className={`w-72 shrink-0 rounded-xl border p-3 transition-colors ${overCol === col ? "border-[#C9A227] bg-[#C9A227]/5" : "border-[#E4E9F3] bg-[#F7F9FE]/40"}`}
          >
            <div className="mb-3 flex items-center justify-between px-1">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5A6B8C]">{col}</span>
              <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-[#5A6B8C] ring-1 ring-[#E4E9F3]">{cards.length}</span>
            </div>
            <div className="space-y-3">
              {cards.map((i) => (
                <div key={i.id}>
                  <div draggable onDragStart={() => setDragId(i.id)} onClick={() => onOpen(i)} data-testid={`investor-card-${i.id}`}
                    role="button" tabIndex={0} aria-label={`View investor: ${i.name}`}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(i); } }}
                    className={`cursor-pointer rounded-lg border bg-white p-3 shadow-sm transition-colors hover:border-[#C9A227]/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227]/50 ${isOverdueFollowup(i, today) ? "border-[#DC2626]/40" : "border-[#E4E9F3]"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium leading-snug text-[#14245F]">{i.name}</p>
                      <StatusBadge status={priorityBadge(i.priority)} label={i.priority} />
                    </div>
                    {i.contact_person && <p className="mt-0.5 flex items-center gap-1 text-xs text-[#5A6B8C]"><User size={11} /> {i.contact_person}</p>}
                    <div className="mt-2 flex items-center justify-between">
                      <RelevanceScore value={i.relevance} size={12} />
                      <span className="text-xs font-semibold text-[#14245F]">{gbp(i.potential_amount)}</span>
                    </div>
                    {i.next_follow_up_date && <p className={`mt-1.5 flex items-center gap-1 text-[11px] ${isOverdueFollowup(i, today) ? "font-semibold text-[#DC2626]" : "text-[#5A6B8C]"}`}><Calendar size={11} /> {i.next_follow_up_date}</p>}
                    {i.next_action ? <p className="mt-1 line-clamp-2 text-[11px] text-[#5A6B8C]">→ {i.next_action}</p> : hasNoNextAction(i) && <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-[#A16207]"><AlertTriangle size={11} /> No next action</p>}
                    {i.migration_review_required && <p data-testid={`inv-migration-review-${i.id}`} className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[#B45309]"><AlertTriangle size={11} /> Migration review required</p>}
                  </div>
                  <Select value={i.stage} onValueChange={(v) => onStageChange(i.id, v)}>
                    <SelectTrigger data-testid={`inv-kanban-stage-${i.id}`} className="mt-1.5 h-7 border-[#E4E9F3] bg-white text-[11px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{INVESTOR_STAGES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
              {cards.length === 0 && <p className="px-1 py-5 text-center text-xs text-[#5A6B8C]/60">Drop here</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};
